import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPost, POSTS } from '@/data/blog';
import { getTemplate } from '@/data/templates';
import { TEAM_AUTHOR } from '@/data/authors';
import NewsletterSignup from '@/components/NewsletterSignup';
import * as Icons from 'lucide-react';

const PROD_HOST = 'https://pdf-forms-hub.emergent.host';

function Block({ block }) {
  switch (block.type) {
    case 'h2':
      return <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-10 mb-3">{block.text}</h2>;
    case 'h3':
      return <h3 className="font-display font-bold text-xl tracking-tight mt-7 mb-2">{block.text}</h3>;
    case 'p':
      return <p className="text-[15px] sm:text-base leading-relaxed text-stone-700 mb-4">{block.text}</p>;
    case 'list':
      return (
        <ul className="space-y-2 my-4 pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[15px] text-stone-700">
              <Icons.Check size={16} className="text-orange-600 shrink-0 mt-1" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'tip':
      return (
        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-xl p-4 my-5">
          <div className="text-xs uppercase tracking-wider text-orange-700 font-bold mb-1.5 flex items-center gap-1.5">
            <Icons.Lightbulb size={13} /> Tip
          </div>
          <div className="text-[15px] text-stone-800 leading-relaxed">{block.text}</div>
        </div>
      );
    case 'code':
      return (
        <pre className="bg-stone-900 text-stone-100 rounded-xl p-5 overflow-x-auto my-5 text-sm font-mono leading-relaxed whitespace-pre-wrap">{block.text}</pre>
      );
    case 'cta':
      return (
        <Link
          to={block.href}
          className="inline-flex items-center gap-2 my-5 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full text-sm font-medium btn-clay"
        >
          <Icons.Sparkles size={14} /> {block.label}
        </Link>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const navigate = useNavigate();

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display font-bold text-3xl">Post not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-orange-600 underline">All posts</Link>
      </div>
    );
  }

  const relatedTemplate = post.relatedTemplate ? getTemplate(post.relatedTemplate) : null;
  const url = `${PROD_HOST}/blog/${post.slug}`;
  const related = POSTS.filter(p => p.slug !== post.slug).slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Organization', name: 'Everything PDF' },
    publisher: { '@type': 'Organization', name: 'Everything PDF' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  return (
    <article data-testid={`blog-post-${post.slug}`}>
      <Helmet>
        <title>{`${post.title} | Everything PDF`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${PROD_HOST}/og-default.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={`${PROD_HOST}/og-default.png`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="max-w-3xl mx-auto px-6 pt-12 pb-8">
        <Link to="/blog" className="text-sm text-stone-500 hover:text-orange-600 inline-flex items-center gap-1 mb-8" data-testid="blog-back">
          <Icons.ArrowLeft size={14} /> All posts
        </Link>
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {post.tags.map((t, i) => (
            <span key={i} className="text-[10px] uppercase tracking-wider font-bold bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full">{t}</span>
          ))}
          <span className="text-xs text-stone-400">{post.readTime}</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">{post.title}</h1>
        <p className="text-stone-600 mt-5 text-lg leading-relaxed">{post.excerpt}</p>

        {/* Author + date row */}
        <div className="flex items-center gap-3 mt-7">
          <div className="w-10 h-10 rounded-full bg-stone-900 text-white grid place-items-center text-sm font-bold font-mono">
            {TEAM_AUTHOR.initials}
          </div>
          <div className="text-sm">
            <div className="font-medium text-stone-900">{TEAM_AUTHOR.name}</div>
            <div className="text-xs text-stone-500">
              Published {new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} · {post.readTime}
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-3xl mx-auto px-6 py-4">
        {post.body.map((b, i) => <Block key={i} block={b} />)}

        {/* Inline newsletter mid-article */}
        <NewsletterSignup source={`post:${post.slug}`} variant="inline" />
      </section>

      {/* RELATED TEMPLATE CTA */}
      {relatedTemplate && (
        <section className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-stone-900 text-white rounded-3xl p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-orange-500/20 blur-3xl rounded-full" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-2">Use this in your own workflow</div>
              <h3 className="font-display font-bold text-2xl tracking-tight">Free {relatedTemplate.title}</h3>
              <p className="text-stone-300 mt-2 text-sm leading-relaxed">{relatedTemplate.description}</p>
              <button
                onClick={() => navigate(`/free-${relatedTemplate.id}-template`)}
                data-testid="blog-related-cta"
                className="inline-flex items-center gap-2 mt-5 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-full text-sm font-medium btn-clay"
              >
                <Icons.Sparkles size={14} /> Generate Yours Free
              </button>
            </div>
          </div>
        </section>
      )}

      {/* MORE POSTS */}
      {related.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 py-10 pb-20">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-4">Keep reading</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map(p => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="block bg-white border border-stone-200 rounded-2xl p-5 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider font-bold text-stone-500">
                  {p.tags[0]} · {p.readTime}
                </div>
                <h4 className="font-display font-bold text-lg tracking-tight leading-snug">{p.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
